// src/components/Admin/JobFormModal.jsx
import React, { useState, useEffect } from 'react';
import { provincesWithCities, getCitiesByProvince } from '../../utils/cities';
import { EXPERIENCE_LEVEL_OPTIONS } from '../../utils/experienceLevels';

const JobFormModal = ({ isOpen, onClose, onSave, initialData }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        requirements: '',
        salary: '',
        closingDate: '',
        province: '',
        city: '',
        workType: '',
        experienceLevel: '',
        applyLink: '',
        company: '',
        isSponsored: false,
        jobType: 'external'
    });
    const [availableCities, setAvailableCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Update cities when province changes
    useEffect(() => {
        if (formData.province) {
            const cities = getCitiesByProvince(formData.province);
            setAvailableCities(cities);
            if (formData.city && !cities.includes(formData.city)) {
                setFormData(prev => ({ ...prev, city: '' }));
            }
        } else {
            setAvailableCities([]);
            setFormData(prev => ({ ...prev, city: '' }));
        }
    }, [formData.province]);

    useEffect(() => {
        if (initialData) {
            setFormData({
                title: initialData.title || '',
                description: initialData.description || '',
                requirements: initialData.requirements || '',
                salary: initialData.salary || '',
                closingDate: initialData.closingDate || '',
                province: initialData.province || '',
                city: initialData.city || '',
                workType: initialData.workType || '',
                experienceLevel: initialData.experienceLevel || '',
                applyLink: initialData.applyLink || '',
                company: initialData.company || '',
                isSponsored: initialData.isSponsored || false,
                jobType: initialData.jobType || 'external'
            });
        } else {
            setFormData({
                title: '',
                description: '',
                requirements: '',
                salary: '',
                closingDate: '',
                province: '',
                city: '',
                workType: '',
                experienceLevel: '',
                applyLink: '',
                company: '',
                isSponsored: false,
                jobType: 'external'
            });
        }
    }, [initialData, isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ 
            ...formData, 
            [name]: type === 'checkbox' ? checked : value 
        });
    };

    const handleProvinceChange = (e) => {
        const province = e.target.value;
        setFormData({ ...formData, province, city: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (!formData.title || !formData.description || !formData.closingDate || !formData.province || !formData.workType) {
            setError('Please fill in all required fields');
            setLoading(false);
            return;
        }

        const result = await onSave(formData);
        if (!result.success) {
            setError(result.error);
        }
        setLoading(false);
    };

    const provincesList = Object.keys(provincesWithCities);
    const workTypes = ['Remote', 'Hybrid', 'On-site'];
    const jobTypes = [
        { value: 'external', label: 'External (Aggregated)' },
        { value: 'internal', label: 'Internal (Recruiter Posted)' }
    ];

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 text-2xl z-10">
                    <i className="fas fa-times"></i>
                </button>

                <div className="p-6">
                    <h2 className="text-2xl font-bold text-gradpath-dark mb-4">
                        {initialData ? 'Edit Job' : 'Post New Job'}
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Job Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Job Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="jobType"
                                value={formData.jobType}
                                onChange={handleChange}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                required
                            >
                                {jobTypes.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        </div>

                        <input
                            type="text"
                            name="title"
                            placeholder="Job Title *"
                            value={formData.title}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            required
                        />
                        
                        <textarea
                            name="description"
                            placeholder="Job Description *"
                            value={formData.description}
                            onChange={handleChange}
                            rows="5"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            required
                        />
                        
                        <textarea
                            name="requirements"
                            placeholder="Requirements (Optional)"
                            value={formData.requirements}
                            onChange={handleChange}
                            rows="4"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        />
                        
                        <input
                            type="text"
                            name="salary"
                            placeholder="Salary (e.g., R250,000 - R350,000)"
                            value={formData.salary}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        />
                        
                        <div className="grid md:grid-cols-2 gap-4">
                            <input
                                type="date"
                                name="closingDate"
                                value={formData.closingDate}
                                onChange={handleChange}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                required
                            />
                            
                            <select
                                name="workType"
                                value={formData.workType}
                                onChange={handleChange}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                required
                            >
                                <option value="">Select Work Type *</option>
                                {workTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>

                        {/* Experience Level */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Experience Level <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="experienceLevel"
                                value={formData.experienceLevel}
                                onChange={handleChange}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                required
                            >
                                <option value="">Select Experience Level *</option>
                                {EXPERIENCE_LEVEL_OPTIONS.map(level => (
                                    <option key={level.value} value={level.value}>{level.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Province *</label>
                                <select
                                    name="province"
                                    value={formData.province}
                                    onChange={handleProvinceChange}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                    required
                                >
                                    <option value="">Select Province *</option>
                                    {provincesList.map(p => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    City
                                    <span className="text-xs text-gray-400 ml-1">(Optional)</span>
                                </label>
                                <select
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    disabled={!formData.province}
                                    className={`w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none ${
                                        !formData.province ? 'bg-gray-100 cursor-not-allowed' : ''
                                    }`}
                                >
                                    <option value="">Select City (Optional)</option>
                                    {availableCities.map(city => (
                                        <option key={city} value={city}>{city}</option>
                                    ))}
                                </select>
                                {!formData.province && (
                                    <p className="text-xs text-gray-400 mt-1">Please select a province first</p>
                                )}
                            </div>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                            <input
                                type="url"
                                name="applyLink"
                                placeholder="Application Link *"
                                value={formData.applyLink}
                                onChange={handleChange}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                required
                            />
                            
                            <input
                                type="text"
                                name="company"
                                placeholder="Company Name"
                                value={formData.company}
                                onChange={handleChange}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            />
                        </div>

                        {/* SPONSOR OPTION */}
                        <div className="border-t border-gray-200 pt-4 mt-2">
                            <div className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    name="isSponsored"
                                    checked={formData.isSponsored || false}
                                    onChange={handleChange}
                                    className="mt-1 w-4 h-4 text-gradpath-green rounded focus:ring-gradpath-green"
                                />
                                <div>
                                    <label className="font-medium text-gray-700 cursor-pointer">
                                        Sponsor this job
                                    </label>
                                    <p className="text-xs text-gray-500">
                                        Job will be featured at the top of the job board with a "Sponsored" badge.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        <div className="flex gap-4 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-gradpath-green text-white px-4 py-2 rounded-lg hover:bg-gradpath-dark transition-all disabled:opacity-50"
                            >
                                {loading ? <i className="fas fa-spinner fa-spin"></i> : (initialData ? 'Update Job' : 'Post Job')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default JobFormModal;